import React, { useState, useEffect } from 'react';

const Footer: React.FC = () => {
  const [username, setUsername] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [hasKeychain, setHasKeychain] = useState<boolean>(false);

  useEffect(() => {
    const checkKeychain = () => {
      if ((window as any).hive_keychain) {
        setHasKeychain(true);
      }
    };
    
    checkKeychain();
    // Re-check for late injection from extension
    const timer = setTimeout(checkKeychain, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !message || !(window as any).hive_keychain) return;

    setStatus('loading');
    
    try {
      // Ensure the memo starts with # for blockchain-level encryption
      const secureMemo = message.trim().startsWith('#') ? message.trim() : `#${message.trim()}`;
      
      // Using the direct bridge method: requestTransfer(account, to, amount, memo, currency, callback)
      (window as any).hive_keychain.requestTransfer(
        username.toLowerCase().trim(), // from
        'louis88',                      // to
        '0.001',                        // amount
        secureMemo,                     // memo
        'HIVE',                         // currency
        (response: any) => {
          if (response.success) {
            setStatus('success');
            setMessage('');
            setUsername('');
            setTimeout(() => setStatus('idle'), 5000);
          } else {
            console.warn("Keychain Transfer Refused:", response.message);
            setStatus('error');
            setTimeout(() => setStatus('idle'), 3000);
          }
        }
      );
    } catch (error: any) {
      console.error("Keychain Bridge Error:", error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <footer id="contact" className="max-w-[1600px] mx-auto border-x border-zinc-800 pb-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 border-b border-zinc-800">
        {/* Brand & Connections */}
        <div className="lg:col-span-7 p-12 border-b lg:border-b-0 lg:border-r border-zinc-800">
          <h2 className="font-mono text-xs text-red-600 uppercase tracking-[0.4em] mb-8">Outbound</h2>
          <h3 className="text-6xl font-black text-white tracking-tighter uppercase mb-12 leading-none">Let's build <br /> together.</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2">
            <a href="https://peakd.com/@louis88" target="_blank" rel="noopener noreferrer" className="p-8 border border-zinc-800 hover:bg-zinc-900 transition-all flex flex-col gap-4 no-underline group">
              <div className="font-mono text-[10px] text-zinc-600 uppercase tracking-widest">Ecosystem_Node</div>
              <div className="text-white font-bold text-lg group-hover:text-red-600 flex items-center justify-between">
                PeakD @louis88
                <i className="fa-brands fa-hive opacity-20 group-hover:opacity-100 transition-opacity"></i>
              </div>
            </a>

            <div className="grid grid-cols-3 border-t sm:border-t-0 sm:border-l border-zinc-800">
              <a href="https://github.com/louis-88" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center hover:bg-zinc-900 hover:text-red-600 transition-all text-xl py-6 no-underline text-white border-r border-zinc-800">
                <i className="fa-brands fa-github"></i>
              </a>
              <a href="https://x.com/louis8820" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center hover:bg-zinc-900 hover:text-red-600 transition-all text-xl py-6 no-underline text-white border-r border-zinc-800">
                <i className="fa-brands fa-x-twitter"></i>
              </a>
              <div className="flex flex-col items-center justify-center hover:bg-zinc-900 transition-all py-6 text-white gap-1.5 group cursor-default">
                <i className="fa-brands fa-discord text-xl group-hover:text-red-600 transition-colors"></i>
                <span className="font-mono text-[8px] uppercase tracking-widest text-zinc-600 group-hover:text-zinc-400">louis88</span>
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic Secure Form */}
        <div className="lg:col-span-5 p-12 bg-zinc-950/50 flex flex-col justify-center min-h-[400px]">
          {hasKeychain ? (
            <form className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-700" onSubmit={handleTransfer}>
              <div className="font-mono text-[10px] text-green-500 uppercase tracking-[0.3em] mb-4 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                Secure_Bridge_Active
              </div>
              
              <div className="space-y-2">
                <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-widest">Identification (Your Username)</label>
                <input 
                  type="text" 
                  required
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="HIVE_ACCOUNT" 
                  className="w-full bg-zinc-900/50 border border-zinc-800 p-4 text-white focus:outline-none focus:border-red-600 font-mono text-sm" 
                />
              </div>

              <div className="space-y-2">
                <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-widest">Transmission_Message</label>
                <textarea 
                  required
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={4} 
                  placeholder="MESSAGE_CONTENT..." 
                  className="w-full bg-zinc-900/50 border border-zinc-800 p-4 text-white focus:outline-none focus:border-red-600 font-mono text-sm resize-none"
                ></textarea>
              </div>

              <button 
                type="submit"
                disabled={status === 'loading'}
                className={`w-full py-4 font-mono text-xs font-bold uppercase tracking-widest transition-all border-none cursor-pointer flex items-center justify-center gap-3 ${
                  status === 'loading' ? 'bg-zinc-800 text-zinc-500 cursor-wait' :
                  status === 'success' ? 'bg-green-600 text-white' :
                  status === 'error' ? 'bg-zinc-800 text-red-500 border border-red-500/20' :
                  'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {status === 'loading' && <i className="fa-solid fa-circle-notch animate-spin"></i>}
                {status === 'success' && <i className="fa-solid fa-check"></i>}
                {status === 'error' && <i className="fa-solid fa-triangle-exclamation"></i>}
                
                {status === 'idle' && 'Send_Encrypted_Ping'}
                {status === 'loading' && 'Awaiting_Signature...'}
                {status === 'success' && 'Transmission_Verified'}
                {status === 'error' && 'Retry_Broadcast'}
              </button>

              <p className="text-[9px] font-mono text-zinc-600 uppercase tracking-tighter text-center">
                * Messaging utilizes a 0.001 HIVE transfer for on-chain delivery.
              </p>
            </form>
          ) : (
            <div className="text-center p-8 border border-zinc-800 bg-zinc-900/20 flex flex-col items-center justify-center gap-6 group">
              <div className="w-16 h-16 rounded-full border border-zinc-800 flex items-center justify-center text-zinc-700 group-hover:text-red-600 group-hover:border-red-600/30 transition-all duration-500">
                <i className="fa-solid fa-lock text-2xl"></i>
              </div>
              <div className="space-y-2">
                <h4 className="font-mono text-xs text-white uppercase tracking-widest">Handshake_Required</h4>
                <p className="text-zinc-500 text-[10px] font-mono leading-relaxed uppercase tracking-tight max-w-[240px] mx-auto">
                  Secure communication requires the <span className="text-red-600">Hive Keychain</span> extension for identity verification.
                </p>
              </div>
              <a 
                href="https://hive-keychain.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-mono text-[9px] text-zinc-400 hover:text-white border-b border-zinc-800 hover:border-red-600 transition-all no-underline pb-1 uppercase tracking-widest"
              >
                Initialize_Keychain_v1.0
              </a>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-12 px-12 flex flex-col md:flex-row justify-between items-center gap-6 text-[10px] font-mono uppercase text-zinc-600 tracking-[0.2em]">
        <div>ARCHITECT_VER_2.1 // POWERED_BY_HIVE</div>
        <div>© {new Date().getFullYear()} LOUIS-88 // ALL_RIGHTS_RESERVED</div>
      </div>
    </footer>
  );
};

export default Footer;